/**
 * EngineDetailView — full-width detail page for the Engine Server,
 * shown in the main content area when the Engine sidebar view is selected.
 *
 * Connection status, session-scoped execution activity, and protocol reference.
 */

import type { SessionSummary } from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import { CollapsibleSidebar, JsonTree } from "@magic-md/plugin-sdk";
import {
    Activity,
    AlertTriangle,
    ArrowLeft,
    ChevronDown,
    ChevronRight,
    Circle,
    Cpu,
    Filter,
    FileText,
    Layers,
    Loader2,
    PanelLeftClose,
    PanelLeftOpen,
    RefreshCw,
    Server,
    Terminal,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "../lib/utils";
import type { EngineStatus } from "../platform";
import { useEditorStore } from "../store/editor-store";
import { useEngineOverviewStore } from "../store/engine-overview-store";
import {
    type ExecutionEntry,
    type SessionExecutionSnapshot,
    type ExecutionStatus,
    usePluginExecutionStore,
} from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

/* ── Main Component ── */

export function EngineDetailView() {
    const t = useT("editor");
    const setShowEngineDetail = useEditorStore((s) => s.setShowEngineDetail);

    const activeSessionId = usePluginExecutionStore((s) => s.sessionId);
    const executions = usePluginExecutionStore((s) => s.executions);
    const status = useEngineOverviewStore((s) => s.status);
    const sessions = useEngineOverviewStore((s) => s.sessions);
    const loading = useEngineOverviewStore(
        (s) => s.loadingScopes.status || s.loadingScopes.sessions,
    );
    const error = useEngineOverviewStore((s) => s.error);
    const retainOverviewScopes = useEngineOverviewStore((s) => s.retainScopes);
    const refreshNow = useEngineOverviewStore((s) => s.refreshNow);

    useEffect(() => {
        return retainOverviewScopes(["status", "sessions"]);
    }, [retainOverviewScopes]);

    return (
        <div className="flex flex-col h-full bg-background">
            {/* Top bar — breadcrumb navigation */}
            <div className="flex items-center gap-2 h-10 px-4 border-b border-border shrink-0 bg-muted/20">
                <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowEngineDetail(false)}
                >
                    <ArrowLeft size={14} />
                    <span>{t("common.back")}</span>
                </button>
                <div className="w-px h-4 bg-border" />

                <span className="text-xs text-muted-foreground">{t("engineDetail.server")}</span>

                <div className="flex-1" />
                <button
                    type="button"
                    onClick={() => {
                        void refreshNow(["status", "sessions"]);
                    }}
                    disabled={loading}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                    <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                    <span>{t("common.refresh")}</span>
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-auto">
                <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
                    {/* Error */}
                    {error ? (
                        <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3">
                            <AlertTriangle size={14} className="text-destructive shrink-0" />
                            <span className="text-sm text-destructive">{error}</span>
                        </div>
                    ) : null}

                    {status.ready ? (
                        <>
                            {/* Header */}
                            <HeaderSection status={status} />

                            <EngineActivityWorkspace
                                sessions={sessions}
                                activeSessionId={activeSessionId}
                                executions={executions}
                            />

                            {/* Protocol Info */}
                            <div>
                                <SectionTitle
                                    title={t("sidecarDetail.protocol")}
                                    icon={<Terminal size={14} />}
                                />
                                <ProtocolSection />
                            </div>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                            {status.alive ? (
                                <>
                                    <Activity
                                        size={32}
                                        className="theme-accent-text animate-pulse"
                                        style={getThemeAccentStyle("warning")}
                                    />
                                    <p className="text-sm text-muted-foreground">
                                        {t("engineDetail.starting")}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <Cpu size={32} className="text-muted-foreground/30" />
                                    <p className="text-sm text-muted-foreground">
                                        {t("engineDetail.notRunning")}
                                    </p>
                                    <p className="text-xs text-muted-foreground/50">
                                        {t("engineDetail.autoStartHint")}
                                    </p>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ── Header ── */

function HeaderSection({ status }: { status: EngineStatus }) {
    const t = useT("editor");
    return (
        <div className="flex items-start gap-4">
            <div
                className={cn(
                    "w-14 h-14 rounded-xl border flex items-center justify-center shrink-0",
                    !status.alive
                        ? "border-border bg-muted/30"
                        : status.ready
                            ? "theme-accent-subtle"
                            : "theme-accent-subtle",
                )}
                style={
                    status.alive
                        ? getThemeAccentStyle(status.ready ? "success" : "warning")
                        : undefined
                }
            >
                {!status.alive ? (
                    <Cpu size={24} className="text-muted-foreground/40" />
                ) : status.ready ? (
                    <Server
                        size={24}
                        className="theme-accent-text"
                        style={getThemeAccentStyle("success")}
                    />
                ) : (
                    <Activity
                        size={24}
                        className="theme-accent-text animate-pulse"
                        style={getThemeAccentStyle("warning")}
                    />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <h1 className="text-xl font-bold text-foreground tracking-tight">
                        {t("engineDetail.server")}
                    </h1>
                    <StatusBadge alive={status.alive} ready={status.ready} />
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                    {t("engineDetail.description")}
                </p>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <Badge>JSON-RPC 2.0</Badge>
                    <Badge>stdio</Badge>
                    <Badge>Node.js</Badge>
                </div>
            </div>
        </div>
    );
}

/* ── Session Activity Workspace (Overview Level) ── */

type SessionFilter = "all" | "active" | "withActivity";
type ExecutionStatusFilter = "all" | "success" | "error" | "running" | "cached";

interface SessionActivityItem {
    id: string;
    label: string;
    secondary: string;
    session: SessionSummary;
    active: boolean;
    executions: ExecutionEntry[];
}

function EngineActivityWorkspace({
    sessions,
    activeSessionId,
    executions,
}: {
    sessions: SessionSummary[];
    activeSessionId: string | null;
    executions: Map<string, ExecutionEntry>;
}) {
    const t = useT("editor");
    const sessionExecutionCache = usePluginExecutionStore((s) => s.sessionExecutionCache);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [filter, setFilter] = useState<SessionFilter>("all");
    const [query, setQuery] = useState("");
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
        activeSessionId ?? sessions[0]?.sessionId ?? null,
    );

    const items = useMemo<SessionActivityItem[]>(() => {
        return sessions
            .slice()
            .sort((a, b) => {
                if (a.sessionId === activeSessionId) return -1;
                if (b.sessionId === activeSessionId) return 1;
                return sessionLabel(a).localeCompare(sessionLabel(b));
            })
            .map((session) => {
                const snapshot = getSessionExecutionState(
                    session.sessionId,
                    activeSessionId,
                    executions,
                    sessionExecutionCache,
                );
                return {
                    id: session.sessionId,
                    label: sessionLabel(session),
                    secondary: session.filePath ?? session.sessionId,
                    session,
                    active: session.sessionId === activeSessionId,
                    executions: snapshot.executions,
                };
            });
    }, [activeSessionId, executions, sessionExecutionCache, sessions]);

    useEffect(() => {
        if (selectedSessionId && items.some((item) => item.id === selectedSessionId)) {
            return;
        }
        setSelectedSessionId(activeSessionId ?? items[0]?.id ?? null);
    }, [activeSessionId, items, selectedSessionId]);

    const selectedItem = useMemo(
        () => items.find((item) => item.id === selectedSessionId) ?? null,
        [items, selectedSessionId],
    );

    const filteredItems = useMemo(() => {
        const lowered = query.trim().toLowerCase();
        return items.filter((item) => {
            if (filter === "active" && !item.active) return false;
            if (filter === "withActivity" && item.executions.length === 0) return false;
            if (!lowered) return true;
            return [item.label, item.secondary, item.session.sessionId]
                .filter(Boolean)
                .some((value) => value.toLowerCase().includes(lowered));
        });
    }, [filter, items, query]);

    const totalExecutions = items.reduce((sum, item) => sum + item.executions.length, 0);
    const runningCount = items.reduce(
        (sum, item) => sum + item.executions.filter((entry) => entry.status === "running").length,
        0,
    );
    const failedCount = items.reduce(
        (sum, item) => sum + item.executions.filter((entry) => entry.status === "error").length,
        0,
    );

    return (
        <div>
            <div className="mb-3 flex items-center gap-2">
                <Layers size={14} className="text-muted-foreground" />
                <h2 className="text-sm font-semibold text-foreground">
                    {t("engineDetail.activity.title")}
                </h2>
                <span className="rounded bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground tabular-nums">
                    {sessions.length}
                </span>
                {totalExecutions > 0 ? (
                    <span className="rounded bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground tabular-nums">
                        {t("engineDetail.activity.executionTotal")} {totalExecutions}
                    </span>
                ) : null}
                {runningCount > 0 ? (
                    <span
                        className="theme-accent-chip rounded px-1.5 py-0.5 text-[10px] font-medium"
                        style={getThemeAccentStyle("warning")}
                    >
                        {t("status.running")} {runningCount}
                    </span>
                ) : null}
                {failedCount > 0 ? (
                    <span
                        className="theme-accent-chip rounded px-1.5 py-0.5 text-[10px] font-medium"
                        style={getThemeAccentStyle("error")}
                    >
                        {t("engineDetail.execution.failed")} {failedCount}
                    </span>
                ) : null}
            </div>

            <div className="rounded-lg border border-border bg-card overflow-hidden">
                <CollapsibleSidebar
                    collapsed={sidebarCollapsed}
                    storageKey="engine-detail/session-sidebar"
                    defaultWidth={320}
                    minWidth={260}
                    maxWidth={460}
                    minMainWidth={460}
                    collapsedWidth={42}
                    className="h-[620px] min-h-[420px] min-w-0 overflow-hidden"
                    sidebarClassName="border-r border-border/40 bg-muted/10"
                    mainClassName="h-full min-h-0 min-w-0 bg-card"
                    collapsedSidebar={
                        <button
                            type="button"
                            className="flex h-full w-full items-start justify-center pt-3 text-muted-foreground hover:bg-muted/30 hover:text-foreground"
                            onClick={() => setSidebarCollapsed(false)}
                            title={t("common.expand")}
                        >
                            <PanelLeftOpen size={14} />
                        </button>
                    }
                    sidebar={
                        <aside className="flex h-full min-h-0 flex-col">
                            <div className="border-b border-border/40 p-3 space-y-2">
                                <div className="flex items-center gap-2">
                                    <FileText size={12} className="text-muted-foreground shrink-0" />
                                    <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground/80">
                                        {t("sessionDetail.sessions")}
                                    </span>
                                    <button
                                        type="button"
                                        className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                                        onClick={() => setSidebarCollapsed(true)}
                                        title={t("common.collapse")}
                                    >
                                        <PanelLeftClose size={12} />
                                    </button>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <Filter size={11} className="text-muted-foreground/50 shrink-0" />
                                    <input
                                        type="text"
                                        placeholder={t("engineDetail.activity.filterPlaceholder")}
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/40 outline-none"
                                    />
                                </div>
                                <div className="grid grid-cols-3 rounded-md border border-border/50 overflow-hidden text-[10px]">
                                    {(["all", "active", "withActivity"] as SessionFilter[]).map(
                                        (nextFilter) => (
                                            <button
                                                key={nextFilter}
                                                type="button"
                                                className={cn(
                                                    "px-2 py-1 transition-colors",
                                                    filter === nextFilter
                                                        ? "bg-primary/10 text-primary font-medium"
                                                        : "text-muted-foreground hover:text-foreground hover:bg-muted/30",
                                                )}
                                                onClick={() => setFilter(nextFilter)}
                                            >
                                                {sessionFilterLabel(nextFilter, t)}
                                            </button>
                                        ),
                                    )}
                                </div>
                            </div>

                            <div className="min-h-0 flex-1 overflow-auto divide-y divide-border/20">
                                {filteredItems.length === 0 ? (
                                    <div className="px-3 py-6 text-center">
                                        <p className="text-xs text-muted-foreground/50">
                                            {t("engineDetail.activity.empty")}
                                        </p>
                                    </div>
                                ) : (
                                    filteredItems.map((item) => (
                                        <SessionActivityRow
                                            key={item.id}
                                            item={item}
                                            selected={selectedSessionId === item.id}
                                            onSelect={() => setSelectedSessionId(item.id)}
                                        />
                                    ))
                                )}
                            </div>
                        </aside>
                    }
                >
                    {selectedItem ? (
                        <SessionActivityPanel item={selectedItem} />
                    ) : (
                        <div className="flex h-full min-h-0 items-center justify-center p-6 text-center">
                            <p className="text-xs text-muted-foreground/50">
                                {t("engineDetail.activity.noSessionSelected")}
                            </p>
                        </div>
                    )}
                </CollapsibleSidebar>
            </div>
        </div>
    );
}

function SessionActivityRow({
    item,
    selected,
    onSelect,
}: {
    item: SessionActivityItem;
    selected: boolean;
    onSelect: () => void;
}) {
    const t = useT("editor");
    const runningCount = item.executions.filter((entry) => entry.status === "running").length;
    const failedCount = item.executions.filter((entry) => entry.status === "error").length;

    return (
        <button
            type="button"
            className={cn(
                "flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors",
                selected
                    ? "bg-primary/10 text-foreground"
                    : "hover:bg-muted/20 text-muted-foreground",
            )}
            onClick={onSelect}
        >
            <span
                className={cn(
                    "h-2 w-2 rounded-full shrink-0",
                    item.active ? "theme-accent-fill" : "bg-muted-foreground/20",
                )}
                style={item.active ? getThemeAccentStyle("success") : undefined}
            />
            <FileText size={12} className="shrink-0 opacity-70" />
            <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-medium">{item.label}</div>
                <div className="truncate text-[10px] text-muted-foreground/50">
                    {item.secondary}
                </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
                <span className="text-[10px] font-mono tabular-nums opacity-60">
                    {item.executions.length}
                </span>
                {runningCount > 0 ? (
                    <span
                        className="theme-accent-chip rounded px-1 py-0.5 text-[8px] font-semibold uppercase"
                        style={getThemeAccentStyle("warning")}
                    >
                        {runningCount}
                    </span>
                ) : null}
                {failedCount > 0 ? (
                    <span
                        className="theme-accent-chip rounded px-1 py-0.5 text-[8px] font-semibold uppercase"
                        style={getThemeAccentStyle("error")}
                    >
                        {failedCount}
                    </span>
                ) : null}
                {item.active ? (
                    <span
                        className="theme-accent-chip rounded px-1 py-0.5 text-[8px] font-semibold uppercase"
                        style={getThemeAccentStyle("success")}
                    >
                        {t("common.active")}
                    </span>
                ) : null}
            </div>
        </button>
    );
}

function SessionActivityPanel({
    item,
}: {
    item: SessionActivityItem;
}) {
    const t = useT("editor");
    const [statusFilter, setStatusFilter] = useState<ExecutionStatusFilter>("all");
    const [nameFilter, setNameFilter] = useState("");
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const filteredExecutions = useMemo(() => {
        const lowered = nameFilter.trim().toLowerCase();
        return item.executions.filter((entry) => {
            if (statusFilter !== "all" && entry.status !== statusFilter) return false;
            if (!lowered) return true;
            return [entry.unitId, entry.actionName]
                .filter((value): value is string => Boolean(value))
                .some((value) => value.toLowerCase().includes(lowered));
        });
    }, [item.executions, nameFilter, statusFilter]);

    const statusCounts = useMemo(() => {
        const counts: Record<ExecutionStatusFilter, number> = {
            all: item.executions.length,
            success: 0,
            error: 0,
            running: 0,
            cached: 0,
        };
        for (const entry of item.executions) {
            if (entry.status === "success") counts.success++;
            else if (entry.status === "error") counts.error++;
            else if (entry.status === "running") counts.running++;
            else if (entry.status === "cached") counts.cached++;
        }
        return counts;
    }, [item.executions]);

    const toggleExpand = (unitId: string) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(unitId)) next.delete(unitId);
            else next.add(unitId);
            return next;
        });
    };

    return (
        <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-card">
            <div className="border-b border-border/40 p-3 space-y-2">
                <div className="flex items-center gap-2 min-w-0">
                    <FileText size={14} className="text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                            <h3 className="min-w-0 truncate text-sm font-semibold text-foreground">
                                {item.label}
                            </h3>
                            <code
                                className="shrink truncate text-[10px] font-mono text-muted-foreground/45"
                                title={item.session.sessionId}
                            >
                                {shortId(item.session.sessionId)}
                            </code>
                        </div>
                        <p className="mt-0.5 truncate text-[10px] text-muted-foreground/50">
                            {item.session.filePath ?? item.session.sessionId}
                        </p>
                    </div>
                    <span className="shrink-0 rounded bg-muted/40 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground tabular-nums">
                        {item.executions.length}
                    </span>
                    {item.active ? (
                        <span
                            className="theme-accent-chip shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium"
                            style={getThemeAccentStyle("success")}
                        >
                            {t("common.active")}
                        </span>
                    ) : null}
                </div>

                <div className="flex flex-col gap-2 xl:flex-row xl:items-center">
                    <div className="flex items-center rounded-md border border-border/50 overflow-hidden text-[10px]">
                        {(["all", "success", "error", "running", "cached"] as ExecutionStatusFilter[]).map(
                            (filter) => (
                                <button
                                    key={filter}
                                    type="button"
                                    className={cn(
                                        "px-2 py-1 transition-colors",
                                        statusFilter === filter
                                            ? "bg-primary/10 text-primary font-medium"
                                            : "text-muted-foreground hover:text-foreground hover:bg-muted/30",
                                    )}
                                    onClick={() => setStatusFilter(filter)}
                                >
                                    {executionStatusFilterLabel(filter, t)}
                                    <span className="ml-1 opacity-50">{statusCounts[filter]}</span>
                                </button>
                            ),
                        )}
                    </div>

                    <div className="flex min-w-0 flex-1 items-center gap-1">
                        <Filter size={11} className="text-muted-foreground/50 shrink-0" />
                        <input
                            type="text"
                            placeholder={t("engineDetail.execution.filterPlaceholder")}
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                            className="min-w-0 flex-1 bg-transparent text-xs text-foreground placeholder:text-muted-foreground/40 outline-none"
                        />
                    </div>
                </div>
            </div>

            {item.executions.length === 0 ? (
                <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-center">
                    <p className="text-xs text-muted-foreground/50">
                        {t("engineDetail.execution.empty")}
                    </p>
                </div>
            ) : filteredExecutions.length === 0 ? (
                <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-center">
                    <p className="text-xs text-muted-foreground/50">
                        {t("engineDetail.execution.noMatches")}
                    </p>
                </div>
            ) : (
                <div className="min-h-0 flex-1 overflow-auto divide-y divide-border/20">
                    {filteredExecutions.map((entry, index) => (
                        <SessionExecutionRow
                            key={entry.unitId}
                            entry={entry}
                            expanded={
                                expanded.has(entry.unitId) ||
                                (expanded.size === 0 && index === 0)
                            }
                            onToggle={() => toggleExpand(entry.unitId)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function SessionExecutionRow({
    entry,
    expanded,
    onToggle,
}: {
    entry: ExecutionEntry;
    expanded: boolean;
    onToggle: () => void;
}) {
    const t = useT("editor");
    const errorMessage = getExecutionErrorMessage(entry);
    const hasDetails = Boolean(errorMessage || entry.output || entry.progress);
    const timeStr = formatExecutionTime(entry.startedAt);

    return (
        <div className="bg-card hover:bg-muted/20 transition-colors">
            <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left"
                onClick={onToggle}
            >
                {expanded ? (
                    <ChevronDown
                        size={10}
                        className="text-muted-foreground/40 shrink-0"
                    />
                ) : (
                    <ChevronRight
                        size={10}
                        className="text-muted-foreground/40 shrink-0"
                    />
                )}
                <ExecutionStatusIcon status={entry.status} />
                <span className="text-[10px] text-muted-foreground/60 font-mono tabular-nums shrink-0">
                    {timeStr}
                </span>
                {entry.actionName ? (
                    <span className="text-[11px] font-mono text-muted-foreground/50 shrink-0">
                        {entry.actionName}
                    </span>
                ) : null}
                <span className="min-w-0 truncate text-xs font-mono font-medium text-foreground/80">
                    {entry.unitId}
                </span>
                <span className="flex-1" />
                {entry.duration != null ? (
                    <span className="text-[10px] text-muted-foreground/40 font-mono tabular-nums shrink-0">
                        {entry.duration}ms
                    </span>
                ) : null}
            </button>

            {expanded ? (
                <div className="px-3 pb-3 space-y-2">
                    {entry.progress ? (
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">
                                    {t("engineDetail.activity.progress")}
                                </span>
                                {entry.progress.percent != null ? (
                                    <span className="text-[10px] text-muted-foreground/40">
                                        {Math.round(entry.progress.percent)}%
                                    </span>
                                ) : null}
                            </div>
                            <div className="bg-muted/20 rounded px-2 py-1.5 overflow-auto max-h-40 mt-0.5">
                                <p className="text-[10px] text-muted-foreground/80">
                                    {entry.progress.message}
                                </p>
                            </div>
                        </div>
                    ) : null}

                    {entry.output ? (
                        <div>
                            <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider">
                                {t("engineDetail.execution.output")}
                            </span>
                            <div className="bg-muted/20 rounded px-2 py-1.5 overflow-auto max-h-56 mt-0.5">
                                <JsonTree value={entry.output} defaultExpandDepth={2} />
                            </div>
                        </div>
                    ) : null}

                    {errorMessage ? (
                        <div>
                            <span
                                className="theme-accent-text text-[10px] uppercase tracking-wider"
                                style={getThemeAccentStyle("error")}
                            >
                                {t("common.error")}
                            </span>
                            <pre
                                className="theme-accent-banner mt-0.5 max-h-20 overflow-auto rounded px-2 py-1.5 text-[10px] font-mono"
                                style={getThemeAccentStyle("error")}
                            >
                                {errorMessage}
                            </pre>
                        </div>
                    ) : null}

                    {!hasDetails ? (
                        <div className="rounded bg-muted/15 px-2 py-1.5 text-[10px] text-muted-foreground/50">
                            {t("engineDetail.activity.noExecutionDetail")}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function getSessionExecutionState(
    sessionId: string,
    activeSessionId: string | null,
    liveExecutions: Map<string, ExecutionEntry>,
    cache: Map<string, SessionExecutionSnapshot>,
): Pick<SessionActivityItem, "executions"> {
    if (sessionId === activeSessionId) {
        return {
            executions: sortExecutionEntries(Array.from(liveExecutions.values())),
        };
    }
    const snapshot = cache.get(sessionId);
    return {
        executions: sortExecutionEntries(Array.from(snapshot?.executions.values() ?? [])),
    };
}

function getExecutionErrorMessage(entry: ExecutionEntry): string | null {
    if (
        entry.status === "error" &&
        entry.result &&
        !entry.result.success &&
        "message" in entry.result
    ) {
        return (entry.result as { message: string }).message;
    }
    return null;
}

function sortExecutionEntries(entries: ExecutionEntry[]): ExecutionEntry[] {
    return entries.slice().sort((a, b) => {
        if (a.status === "running" && b.status !== "running") return -1;
        if (b.status === "running" && a.status !== "running") return 1;
        return (b.startedAt ?? 0) - (a.startedAt ?? 0);
    });
}

function sessionLabel(session: SessionSummary): string {
    if (!session.filePath) return session.sessionId;
    return (
        session.filePath.split("/").pop() ??
        session.filePath.split("\\").pop() ??
        session.filePath
    );
}

function sessionFilterLabel(
    filter: SessionFilter,
    t: ReturnType<typeof useT>,
): string {
    if (filter === "active") return t("engineDetail.activity.activeFilter");
    if (filter === "withActivity") return t("engineDetail.activity.withActivity");
    return t("common.all");
}

function executionStatusFilterLabel(
    filter: ExecutionStatusFilter,
    t: ReturnType<typeof useT>,
): string {
    if (filter === "success") return t("mcpDetail.logs.status.success");
    if (filter === "error") return t("mcpDetail.logs.status.error");
    if (filter === "running") return t("status.running");
    if (filter === "cached") return t("engineDetail.execution.cached");
    return t("common.all");
}

function shortId(id: string): string {
    if (id.length <= 13) return id;
    return `${id.slice(0, 8)}...${id.slice(-4)}`;
}

function formatExecutionTime(value: number | null): string {
    if (!value) return "--:--:--";
    const date = new Date(value);
    return `${date.getHours().toString().padStart(2, "0")}:${date
        .getMinutes()
        .toString()
        .padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
}

function ExecutionStatusIcon({ status }: { status: ExecutionStatus }) {
    switch (status) {
        case "running":
            return (
                <Loader2
                    size={10}
                    className="theme-accent-text animate-spin shrink-0"
                    style={getThemeAccentStyle("warning")}
                />
            );
        case "success":
            return (
                <Circle
                    size={8}
                    className="theme-accent-text fill-current shrink-0"
                    style={getThemeAccentStyle("success")}
                />
            );
        case "error":
            return (
                <Circle
                    size={8}
                    className="theme-accent-text fill-current shrink-0"
                    style={getThemeAccentStyle("error")}
                />
            );
        case "cached":
            return (
                <Circle
                    size={8}
                    className="fill-current text-muted-foreground/40 shrink-0"
                />
            );
        default:
            return <Circle size={8} className="text-muted-foreground/30 shrink-0" />;
    }
}

/* ── Protocol ── */

function ProtocolSection() {
    const t = useT("editor");
    const methods = [
        {
            name: "initialize",
            dir: "C→S",
            desc: "Handshake, exchange capabilities",
        },
        { name: "shutdown", dir: "C→S", desc: "Graceful shutdown" },
        { name: "session/create", dir: "C→S", desc: "Create document session" },
        { name: "session/close", dir: "C→S", desc: "Close session" },
        { name: "session/sync", dir: "C→S", desc: "Sync content changes" },
        { name: "session/getState", dir: "C→S", desc: "Get session state" },
        { name: "session/getOutput", dir: "C→S", desc: "Get one unit output" },
        { name: "session/list", dir: "C→S", desc: "List all sessions" },
        { name: "session/watch", dir: "C→S", desc: "Watch session for changes" },
        { name: "session/unwatch", dir: "C→S", desc: "Stop watching session" },
        { name: "execution/start", dir: "C→S", desc: "Start async execution job" },
        { name: "execution/getState", dir: "C→S", desc: "Read async execution job state" },
        { name: "execution/cancel", dir: "C→S", desc: "Cancel execution" },
        { name: "sidecar/list", dir: "C→S", desc: "List all sidecars with status" },
        { name: "sidecar/shutdown", dir: "C→S", desc: "Shutdown sidecar" },
        { name: "sidecar/spawn", dir: "C→S", desc: "Spawn sidecar process" },
        { name: "sidecar/call", dir: "C→S", desc: "Call sidecar method" },
        { name: "plugin/list", dir: "C→S", desc: "List loaded plugins" },
        { name: "plugin/getManifest", dir: "C→S", desc: "Get plugin manifest" },
        { name: "push/scan", dir: "C→S", desc: "Scan for Play devices" },
        { name: "push/connect", dir: "C→S", desc: "Connect to Play device" },
        {
            name: "push/disconnect",
            dir: "C→S",
            desc: "Disconnect from Play device",
        },
        { name: "push/pair", dir: "C→S", desc: "Pair with Play device" },
        { name: "push/send", dir: "C→S", desc: "Send content to Play device" },
        {
            name: "push/syncStart",
            dir: "C→S",
            desc: "Start live sync to Play device",
        },
        { name: "push/syncStop", dir: "C→S", desc: "Stop live sync" },
        { name: "push/status", dir: "C→S", desc: "Get push connection status" },
        { name: "mcp/start", dir: "C→S", desc: "Start MCP server" },
        { name: "mcp/stop", dir: "C→S", desc: "Stop MCP server" },
        { name: "mcp/status", dir: "C→S", desc: "Get MCP server status" },
        { name: "mcp/logs", dir: "C→S", desc: "Get MCP server logs" },
    ];

    const events = [
        { name: "event/execution.started", desc: "Execution began" },
        { name: "event/execution.progress", desc: "Execution phase update" },
        {
            name: "event/execution.completed",
            desc: "Execution finished with output",
        },
        { name: "event/execution.failed", desc: "Execution error" },
        { name: "event/execution.cascade", desc: "Cascade re-execution triggered" },
        { name: "event/document.changed", desc: "Document content updated" },
        { name: "event/sidecar.spawned", desc: "Sidecar process started" },
        { name: "event/sidecar.stopped", desc: "Sidecar process stopped" },
        { name: "event/sidecar.restarted", desc: "Sidecar process restarted" },
        { name: "event/file.changed", desc: "Watched file changed" },
        { name: "event/log", desc: "Engine Server log message" },
        { name: "event/push.deviceFound", desc: "Play device discovered" },
        { name: "event/push.deviceLost", desc: "Play device lost" },
        {
            name: "event/push.connectionChanged",
            desc: "Push connection state changed",
        },
        {
            name: "event/push.pairingChallenge",
            desc: "Push pairing challenge received",
        },
        { name: "event/push.pairingResult", desc: "Push pairing result" },
        { name: "event/push.ack", desc: "Push message acknowledged" },
        { name: "event/push.syncStatus", desc: "Push sync status update" },
        { name: "event/mcp.statusChanged", desc: "MCP server status changed" },
        { name: "event/mcp.requestLog", desc: "MCP server request log" },
        { name: "event/stream.chunk", desc: "Stream chunk received" },
        { name: "event/stream.state", desc: "Stream state changed" },
        { name: "event/stream.dropSummary", desc: "Stream drop summary" },
    ];

    return (
        <div className="space-y-4">
            {/* RPC Methods */}
            <div>
                <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-semibold">
                    {t("engineDetail.protocol.methods")}
                </span>
                <div className="mt-1 rounded-lg border border-border/40 overflow-hidden">
                    <table className="w-full text-left text-xs">
                        <thead>
                            <tr className="bg-muted/30 border-b border-border/30">
                                <th className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider w-8">
                                    {t("engineDetail.protocol.dir")}
                                </th>
                                <th className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                                    {t("engineDetail.protocol.method")}
                                </th>
                                <th className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                                    {t("common.description")}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/20">
                            {methods.map((m) => (
                                <tr key={m.name} className="hover:bg-muted/10">
                                    <td className="px-3 py-1.5 text-[10px] text-muted-foreground/40 whitespace-nowrap">
                                        {m.dir}
                                    </td>
                                    <td className="px-3 py-1.5">
                                        <code className="text-[10px] font-mono text-primary/70">
                                            {m.name}
                                        </code>
                                    </td>
                                    <td className="px-3 py-1.5 text-[10px] text-muted-foreground/60">
                                        {m.desc}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Events */}
            <div>
                <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-semibold">
                    {t("engineDetail.protocol.events")}
                </span>
                <div className="mt-1 rounded-lg border border-border/40 overflow-hidden">
                    <table className="w-full text-left text-xs">
                        <thead>
                            <tr className="bg-muted/30 border-b border-border/30">
                                <th className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                                    {t("engineDetail.protocol.event")}
                                </th>
                                <th className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider">
                                    {t("common.description")}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/20">
                            {events.map((e) => (
                                <tr key={e.name} className="hover:bg-muted/10">
                                    <td className="px-3 py-1.5">
                                        <code
                                            className="theme-accent-text text-[10px] font-mono"
                                            style={getThemeAccentStyle("warning")}
                                        >
                                            {e.name}
                                        </code>
                                    </td>
                                    <td className="px-3 py-1.5 text-[10px] text-muted-foreground/60">
                                        {e.desc}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Transport Info */}
            <div className="grid grid-cols-3 gap-2">
                <InfoCard label={t("mcpDetail.info.transport")}>
                    <code className="text-[10px] font-mono text-foreground/70">
                        stdio
                    </code>
                </InfoCard>
                <InfoCard label={t("engineDetail.protocol.framing")}>
                    <code className="text-[10px] font-mono text-foreground/70">
                        Content-Length
                    </code>
                </InfoCard>
                <InfoCard label={t("mcpDetail.info.protocol")}>
                    <code className="text-[10px] font-mono text-foreground/70">
                        JSON-RPC 2.0
                    </code>
                </InfoCard>
            </div>
        </div>
    );
}

/* ── Shared Sub-components ── */

function SectionTitle({
    title,
    icon,
}: {
    title: string;
    icon?: React.ReactNode;
}) {
    return (
        <div className="mb-3 flex items-center gap-2">
            {icon ? <span className="text-muted-foreground">{icon}</span> : null}
            <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        </div>
    );
}

function Badge({ children }: { children: React.ReactNode }) {
    return (
        <span className="inline-block rounded-md border border-border/50 bg-muted/30 text-muted-foreground px-1.5 py-0.5 text-[10px]">
            {children}
        </span>
    );
}

function StatusBadge({ alive, ready }: { alive: boolean; ready: boolean }) {
    const t = useT("editor");
    return (
        <span
            className={cn(
                "inline-block rounded-md border px-1.5 py-0.5 text-[10px]",
                !alive
                    ? "border-border/50 bg-muted/30 text-muted-foreground"
                    : ready
                        ? "theme-accent-chip"
                        : "theme-accent-chip",
            )}
            style={
                alive
                    ? getThemeAccentStyle(ready ? "success" : "warning")
                    : undefined
            }
        >
            {!alive
                ? t("engine.status.offline")
                : ready
                    ? t("engine.status.connected")
                    : t("engine.status.starting")}
        </span>
    );
}

function InfoCard({
    label,
    children,
}: {
    label: string;
    children: React.ReactNode;
}) {
    return (
        <div className="rounded-lg border border-border/30 bg-muted/10 px-3 py-2">
            <div className="text-[9px] text-muted-foreground/40 uppercase tracking-wider font-semibold mb-1">
                {label}
            </div>
            {children}
        </div>
    );
}
